import React from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.png";

const sections = [
  {
    title: "AI Credit Analysis",
    bullets: [
      "Identify high-priority derogatory items quickly",
      "Generate intelligent action plans for operators",
      "Surface trends across clients and bureau responses",
    ],
  },
  {
    title: "White-Label Branding",
    bullets: [
      "Custom logo, colors, and domain",
      "Operator-facing and client-facing brand consistency",
      "Configurable chatbot welcome and UI identity",
    ],
  },
  {
    title: "Dispute Engine",
    bullets: [
      "Track dispute lifecycle from intake to resolution",
      "Organize by bureau and status for cleaner operations",
      "Support letter generation and follow-up workflows",
    ],
  },
  {
    title: "Client Portal",
    bullets: [
      "Secure client login experience",
      "Shared visibility into milestones and next actions",
      "Document upload and communication workflows",
    ],
  },
  {
    title: "Automated Workflows",
    bullets: [
      "Rule-based reminders and status transitions",
      "Notification triggers for clients and operators",
      "Reduced manual touchpoints for repeat tasks",
    ],
  },
  {
    title: "Stripe Billing",
    bullets: [
      "Recurring subscriptions and plan changes",
      "Centralized invoice and payment tracking",
      "Built-in support for compliant billing operations",
    ],
  },
];

const FeaturesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-950/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="IntelliWarrior" className="h-8 w-8 rounded-full" />
            <span className="text-lg font-semibold">IntelliWarrior</span>
          </Link>
          <div className="flex gap-4 text-sm text-slate-300">
            <Link to="/pricing" className="hover:text-cyan-300">
              Pricing
            </Link>
            <Link to="/login" className="hover:text-cyan-300">
              Login
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-white">Platform features for modern credit repair teams</h1>
        <p className="mt-3 max-w-2xl text-slate-300">
          IntelliWarrior combines AI, operations tooling, branding, and client experience to help agencies scale with control.
        </p>

        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {sections.map((section) => (
            <section key={section.title} className="rounded-xl border border-slate-800 bg-slate-900/80 p-6">
              <h2 className="text-xl font-semibold text-cyan-200">{section.title}</h2>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                {section.bullets.map((b) => (
                  <li key={b}>� {b}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section className="mt-12 rounded-xl border border-cyan-500/40 bg-gradient-to-r from-blue-600/20 to-cyan-500/20 p-6">
          <h3 className="text-xl font-semibold text-white">Explore all features live</h3>
          <p className="mt-2 text-sm text-slate-200">
            Start your account and see how IntelliWarrior can power client outcomes while keeping your operations compliance-aware.
          </p>
          <Link to="/login" className="mt-4 inline-block rounded-lg bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:brightness-110">
            Get Started
          </Link>
        </section>
      </main>
    </div>
  );
};

export default FeaturesPage;
